import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Book, ArrowRight, BarChart2, Users, DollarSign, Briefcase, Settings, Target, MessageSquare, HelpCircle, Activity } from 'lucide-react';
import PortalBackground from '../components/PortalBackground';

const CourseCard = ({ title, icon, description, progress = 0, path }) => {
  const navigate = useNavigate();
  
  // Get color for progress bar based on progress value
  const getProgressColor = () => {
    if (progress >= 75) return 'bg-green-500';
    if (progress >= 50) return 'bg-yellow-500';
    if (progress >= 25) return 'bg-orange-500';
    return 'bg-red-500';
  };

  return (
    <div 
      onClick={() => path && navigate(path)}
      className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm hover:shadow-md transition-all duration-300 p-6 flex flex-col h-full hover:border-red-200 dark:hover:border-red-800 hover:-translate-y-0.5 cursor-pointer"
    >
      <div className="flex items-center mb-4">
        <div className="p-3 bg-red-50 rounded-md mr-4 text-red-600">
          {icon}
        </div>
        <h3 className="font-bold text-xl text-gray-900">{title}</h3>
      </div>
      <p className="text-gray-600 text-sm mb-6">{description}</p>
      <div className="mt-auto space-y-3">
        <div>
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden mb-2">
            <div 
              className={`${getProgressColor()} h-2 rounded-full transition-all duration-500`} 
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <span className="text-sm text-gray-500">{progress}% complete</span>
        </div>
        <div className="flex items-center text-red-600 text-sm font-medium">
          Start Learning <ArrowRight size={16} className="ml-1" />
        </div>
      </div>
    </div>
  );
};

const categoryData = {
  strategies: {
    title: "Strategies That Scale",
    subtitle: "Master foundational concepts and strategies for sustainable business growth",
    courses: [
      {
        title: "The PD/PM Model",
        icon: <Briefcase size={24} />,
        description: "Master the Production Designer/Project Manager model for efficient business operations.",
        progress: 75,
        path: "/portal/courses/pd-pm-model"
      },
      {
        title: "The Sub Model",
        icon: <Target size={24} />,
        description: "Learn how to effectively work with subcontractors to scale your business.",
        progress: 60,
        path: "/portal/courses/sub-model"
      },
      {
        title: "Business Launch Blueprint",
        icon: <Briefcase size={24} />,
        description: "Create a comprehensive plan to launch or relaunch your remodeling business.",
        progress: 45,
        path: "/portal/courses/business-launch-blueprint"
      },
      {
        title: "Mission, Vision & Values That Drive Growth",
        icon: <Target size={24} />,
        description: "Define your company's purpose and direction to fuel sustainable growth.",
        progress: 30,
        path: "/portal/courses/mission-vision-values"
      }
    ]
  },
  processes: {
    title: "Processes That Win",
    subtitle: "Repeatable Systems to Run Your Business Smarter, Not Harder",
    courses: [
      {
        title: "Lead Flow Mastery",
        icon: <Activity size={24} />,
        description: "Develop a consistent lead generation and qualification system.",
        progress: 25,
        path: "/portal/courses/lead-flow-mastery"
      },
      {
        title: "Sales Accelerator System",
        icon: <DollarSign size={24} />,
        description: "Create a repeatable, scalable sales process for consistent results.",
        progress: 20,
        path: "/portal/courses/sales-accelerator"
      },
      {
        title: "The Ultimate Design Process",
        icon: <HelpCircle size={24} />,
        description: "Master design methodologies for remodeling projects that deliver client satisfaction.",
        progress: 15,
        path: "/portal/courses/ultimate-design"
      },
      {
        title: "The Build Execution Formula",
        icon: <Settings size={24} />,
        description: "Systematize your project execution for predictable, profitable results.",
        progress: 10,
        path: "/portal/courses/build-execution"
      }
    ]
  },
  pillars: {
    title: "The Pillars of Business Success",
    subtitle: "Mastering the 6 Core Areas That Make or Break a Remodeling Business",
    courses: [
      {
        title: "Sales Domination Playbook",
        icon: <DollarSign size={24} />,
        description: "Master proven sales strategies specific to the remodeling industry.",
        progress: 5,
        path: "/portal/courses/sales-domination"
      },
      {
        title: "Operational Excellence",
        icon: <Settings size={24} />,
        description: "Streamline your business operations for maximum efficiency and profitability.",
        progress: 0,
        path: "/portal/courses/operational-excellence"
      },
      {
        title: "Tech & Automation for Remodelers",
        icon: <Settings size={24} />,
        description: "Leverage technology to enhance business capabilities and client experience.",
        progress: 0,
        path: "/portal/courses/tech-automation"
      },
      {
        title: "Financial Mastery for Construction",
        icon: <BarChart2 size={24} />,
        description: "Master the financial aspects of your remodeling business with proven strategies for profitability and growth.",
        progress: 0,
        path: "/portal/courses/financial-mastery"
      },
      {
        title: "Marketing That Actually Works",
        icon: <MessageSquare size={24} />,
        description: "Develop effective marketing strategies specifically for remodeling businesses.",
        progress: 0,
        path: "/portal/courses/marketing-works"
      },
      {
        title: "HR & Hiring Made Simple",
        icon: <Users size={24} />,
        description: "Learn best practices for hiring and managing a team in the remodeling industry.",
        progress: 0,
        path: "/portal/courses/hr-hiring"
      }
    ]
  },
  scaling: {
    title: "Scaling Beyond",
    subtitle: "Building a Self-Sustaining Business That Grows Without You",
    courses: [
      {
        title: "The Scaling Roadmap",
        icon: <Activity size={24} />,
        description: "Chart your path to scaling your remodeling business beyond yourself.",
        progress: 0,
        path: "/portal/courses/scaling-roadmap"
      },
      {
        title: "Hiring & Leadership at Scale",
        icon: <Users size={24} />,
        description: "Strategies for building and leading teams as your remodeling business grows.",
        progress: 0,
        path: "/portal/courses/hiring-leadership"
      }
    ]
  }
};

const CategoryPage = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const category = categoryData[categoryId];

  if (!category) {
    return (
      <div className="relative min-h-screen flex items-center justify-center">
        <PortalBackground />
        <div className="relative text-center">
          <Book size={48} className="mx-auto text-red-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Category not found</h3>
          <button
            onClick={() => navigate('/portal/courses')}
            className="text-red-600 font-medium hover:text-red-800"
          >
            Return to Courses
          </button>
        </div>
      </div>
    );
  }

  // Calculate category completion
  const totalProgress = category.courses.reduce((sum, course) => sum + course.progress, 0);
  const averageProgress = Math.round(totalProgress / category.courses.length);

  return (
    <div className="relative min-h-screen">
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <button
          onClick={() => navigate('/portal/courses')}
          className="flex items-center text-red-600 font-medium mb-6 hover:text-red-800"
        >
          <ArrowLeft size={20} className="mr-2" />
          Back to Categories
        </button>

        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white mb-2">{category.title}</h1>
          <p className="text-gray-600 mb-4">{category.subtitle}</p>
          <div className="flex items-center justify-center">
            <div className="w-48 bg-gray-200 rounded-full h-2 mr-3">
              <div 
                className="h-2 rounded-full bg-red-500"
                style={{ width: `${averageProgress}%` }}
              ></div>
            </div>
            <span className="text-sm text-gray-600">{averageProgress}% complete</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {category.courses.map((course, index) => (
            <CourseCard
              key={index}
              {...course}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default CategoryPage; 